import React, { useState, useEffect } from 'react';
import client from '../api/client';

/**
 * Modal displaying detailed breakdown of an LP tracking run.
 * Shows per-character LP gains, wallet changes, and corporation breakdown.
 */
function LPRunDetailsModal({ runId, onClose }) {
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editingNote, setEditingNote] = useState(false);
    const [noteValue, setNoteValue] = useState('');
    const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
    const [adjustmentType, setAdjustmentType] = useState('wallet_transaction');
    const [adjustmentAmount, setAdjustmentAmount] = useState('');
    const [adjustmentReason, setAdjustmentReason] = useState('');

    useEffect(() => {
        if (runId) {
            fetchDetails();
        }
    }, [runId]);

    const fetchDetails = async () => {
        try {
            setLoading(true);
            const response = await client.get(`/lp/tracking/runs/${runId}/details/`);
            setDetails(response.data.run);
            setNoteValue(response.data.run.notes || '');
            setError(null);
        } catch (err) {
            console.error('Error fetching run details:', err);
            setError('Failed to load run details');
        } finally {
            setLoading(false);
        }
    };

    const saveNote = async () => {
        try {
            await client.patch(`/lp/tracking/runs/${runId}/`, {
                notes: noteValue
            });
            setDetails(prev => ({ ...prev, notes: noteValue }));
            setEditingNote(false);
        } catch (err) {
            console.error('Error saving note:', err);
            alert('Failed to save note');
        }
    };

    const addAdjustment = async () => {
        try {
            const response = await client.post(`/lp/tracking/runs/${runId}/adjustments/`, {
                type: adjustmentType,
                amount: parseFloat(adjustmentAmount),
                reason: adjustmentReason
            });

            // Update details with new adjustment and recalculated metrics
            setDetails(prev => ({
                ...prev,
                manual_adjustments: [...(prev.manual_adjustments || []), response.data.adjustment],
                metrics: response.data.metrics
            }));

            // Reset form
            setAdjustmentAmount('');
            setAdjustmentReason('');
            setShowAdjustmentModal(false);
        } catch (err) {
            console.error('Error adding adjustment:', err);
            alert('Failed to add adjustment');
        }
    };

    const removeAdjustment = async (index) => {
        if (!window.confirm('Are you sure you want to remove this adjustment?')) return;

        try {
            const response = await client.delete(`/lp/tracking/runs/${runId}/adjustments/${index}/`);

            // Update details with updated metrics
            setDetails(prev => ({
                ...prev,
                manual_adjustments: prev.manual_adjustments.filter((_, i) => i !== index),
                metrics: response.data.metrics
            }));
        } catch (err) {
            console.error('Error removing adjustment:', err);
            alert('Failed to remove adjustment');
        }
    };

    const excludeCorp = async (corpId) => {
        try {
            const response = await client.post(`/lp/tracking/runs/${runId}/exclude-corp/${corpId}/`);

            // Update details with new excluded list and recalculated metrics
            setDetails(prev => ({
                ...prev,
                excluded_corp_ids: response.data.excluded_corp_ids,
                metrics: response.data.metrics
            }));
        } catch (err) {
            console.error('Error excluding corporation:', err);
            alert('Failed to exclude corporation');
        }
    };

    const includeCorp = async (corpId) => {
        try {
            const response = await client.delete(`/lp/tracking/runs/${runId}/include-corp/${corpId}/`);

            // Update details with updated excluded list and recalculated metrics
            setDetails(prev => ({
                ...prev,
                excluded_corp_ids: response.data.excluded_corp_ids,
                metrics: response.data.metrics
            }));
        } catch (err) {
            console.error('Error including corporation:', err);
            alert('Failed to include corporation');
        }
    };

    // Aggregate LP totals by corporation across all characters
    const getCorpTotals = () => {
        if (!details || !details.characters) return [];

        const corpMap = {};

        details.characters.forEach(char => {
            if (char.lp_changes) {
                char.lp_changes.forEach(lp => {
                    if (!corpMap[lp.corporation_id]) {
                        corpMap[lp.corporation_id] = {
                            corporation_id: lp.corporation_id,
                            corporation_name: lp.corporation_name,
                            total_lp_gained: 0
                        };
                    }
                    corpMap[lp.corporation_id].total_lp_gained += lp.lp_gained;
                });
            }
        });

        return Object.values(corpMap).sort((a, b) => b.total_lp_gained - a.total_lp_gained);
    };

    const formatDate = (isoString) => {
        return new Date(isoString).toLocaleString();
    };

    const formatDuration = (hours) => {
        const h = Math.floor(hours);
        const m = Math.floor((hours - h) * 60);
        return `${h}h ${m}m`;
    };

    if (!runId) return null;

    const signed = (value, unit) => (
        <b className={value >= 0 ? 'text-pos' : 'text-neg'}>
            {value >= 0 ? '+' : ''}{value.toLocaleString()} {unit}
        </b>
    );

    const closeAdjustment = () => {
        setShowAdjustmentModal(false);
        setAdjustmentAmount('');
        setAdjustmentReason('');
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-wide" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Run #{runId}</h2>
                    {details && <span className="text-faint">{formatDate(details.started_at)}</span>}
                    <button onClick={onClose} className="icon-btn modal-close" aria-label="Close">×</button>
                </div>

                <div className="modal-body">
                    {loading && <div className="text-dim">Loading details...</div>}
                    {error && <div className="text-neg">{error}</div>}

                    {details && (
                        <>
                            <section>
                                <h3 className="section-label">Summary</h3>
                                <div className="stat-grid">
                                    <div className="stat"><span>Started</span><b>{formatDate(details.started_at)}</b></div>
                                    <div className="stat"><span>Ended</span><b>{formatDate(details.ended_at)}</b></div>
                                    <div className="stat"><span>Duration</span><b>{formatDuration(details.metrics.duration_hours)}</b></div>
                                    <div className="stat"><span>Characters</span><b>{details.metrics.character_count}</b></div>
                                    <div className="stat"><span>LP gained</span><b className="text-pos">{details.metrics.lp_gained.toLocaleString()} LP</b></div>
                                    <div className="stat"><span>ISK per LP</span><b>{details.isk_per_lp.toLocaleString()}</b></div>
                                    <div className="stat"><span>Wallet change</span>{signed(details.metrics.wallet_diff, 'ISK')}</div>
                                    {details.metrics.wallet_adjustments !== 0 && (
                                        <div className="stat"><span>Wallet adjustments</span>{signed(details.metrics.wallet_adjustments, 'ISK')}</div>
                                    )}
                                    {details.metrics.misc_adjustments !== 0 && (
                                        <div className="stat"><span>Misc adjustments</span>{signed(details.metrics.misc_adjustments, 'ISK')}</div>
                                    )}
                                    <div className="stat"><span>Total ISK</span><b className="text-pos">{details.metrics.total_isk.toLocaleString()} ISK</b></div>
                                    <div className="stat"><span>ISK per hour</span><b>{details.metrics.isk_per_hour.toLocaleString()}</b></div>
                                    <div className="stat"><span>ISK per hour per character</span><b>{details.metrics.isk_per_hour_per_char.toLocaleString()}</b></div>
                                </div>
                            </section>

                            <section>
                                <div className="lp-section-head">
                                    <h3 className="section-label">Notes</h3>
                                    {!editingNote && (
                                        <button onClick={() => setEditingNote(true)} className="btn btn-ghost btn-sm">Edit</button>
                                    )}
                                </div>
                                {editingNote ? (
                                    <div className="stack lp-note-edit">
                                        <textarea value={noteValue} onChange={(e) => setNoteValue(e.target.value)} rows="3" aria-label="Notes" />
                                        <div className="btn-row">
                                            <button onClick={saveNote} className="btn btn-primary btn-sm">Save</button>
                                            <button
                                                onClick={() => {
                                                    setNoteValue(details.notes || '');
                                                    setEditingNote(false);
                                                }}
                                                className="btn btn-ghost btn-sm"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="lp-note">{details.notes || <span className="text-faint">No notes</span>}</p>
                                )}
                            </section>

                            <section>
                                <div className="lp-section-head">
                                    <h3 className="section-label">Manual adjustments</h3>
                                    <button onClick={() => setShowAdjustmentModal(true)} className="btn btn-sm">Add adjustment</button>
                                </div>
                                {details.manual_adjustments && details.manual_adjustments.length > 0 ? (
                                    <table className="data-table">
                                        <tbody>
                                            {details.manual_adjustments.map((adj, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <span className={`pill ${adj.type === 'wallet_transaction' ? 'pill-accent' : 'pill-neutral'}`}>
                                                            {adj.type === 'wallet_transaction' ? 'Wallet' : 'Misc'}
                                                        </span>
                                                    </td>
                                                    <td className="num">{signed(adj.amount, 'ISK')}</td>
                                                    <td className="text-dim">{adj.reason || 'No reason given'}</td>
                                                    <td className="num">
                                                        <button onClick={() => removeAdjustment(index)} className="btn btn-danger btn-sm row-action">Remove</button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <p className="lp-note text-faint">No manual adjustments</p>
                                )}
                            </section>

                            <section>
                                <h3 className="section-label">Totals by corporation</h3>
                                {getCorpTotals().length > 0 ? (
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>Corporation</th>
                                                <th className="num">LP gained</th>
                                                <th className="num">Value</th>
                                                <th aria-label="Actions"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {getCorpTotals().map(corp => {
                                                const isExcluded = details.excluded_corp_ids && details.excluded_corp_ids.includes(corp.corporation_id);
                                                return (
                                                    <tr key={corp.corporation_id} className={isExcluded ? 'lp-excluded-row' : ''}>
                                                        <td>{corp.corporation_name}</td>
                                                        <td className="num">{corp.total_lp_gained.toLocaleString()}</td>
                                                        <td className="num">{(corp.total_lp_gained * details.isk_per_lp).toLocaleString()} ISK</td>
                                                        <td className="num">
                                                            <button
                                                                onClick={() => isExcluded ? includeCorp(corp.corporation_id) : excludeCorp(corp.corporation_id)}
                                                                className="btn btn-sm"
                                                            >
                                                                {isExcluded ? 'Include' : 'Exclude'}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                ) : (
                                    <p className="lp-note text-faint">No corporation LP data</p>
                                )}
                            </section>

                            <section>
                                <h3 className="section-label">By character</h3>
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>Character / corporation</th>
                                            <th className="num">LP gained</th>
                                            <th className="num">Wallet change</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {details.characters && details.characters.map(char => (
                                            <React.Fragment key={char.character_id}>
                                                <tr className="lp-char-row">
                                                    <td>
                                                        <span className="lp-char">
                                                            {char.portrait_url && <img src={char.portrait_url} alt="" className="char-chip-portrait" />}
                                                            <b>{char.character_name}</b>
                                                        </span>
                                                    </td>
                                                    <td className="num"><b className="text-pos">{char.total_lp_gained.toLocaleString()}</b></td>
                                                    <td className="num">{signed(char.wallet_diff, 'ISK')}</td>
                                                </tr>
                                                {char.lp_changes && char.lp_changes.map(lp => (
                                                    <tr key={lp.corporation_id}>
                                                        <td className="lp-corp">{lp.corporation_name}</td>
                                                        <td className={`num ${lp.lp_gained > 0 ? 'text-pos' : 'text-neg'}`}>
                                                            {lp.lp_gained > 0 ? '+' : ''}{lp.lp_gained.toLocaleString()}
                                                        </td>
                                                        <td></td>
                                                    </tr>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </section>
                        </>
                    )}
                </div>

                {showAdjustmentModal && (
                    <div className="modal-overlay" onClick={closeAdjustment}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3>Add manual adjustment</h3>
                                <button onClick={closeAdjustment} className="icon-btn modal-close" aria-label="Close">×</button>
                            </div>
                            <div className="modal-body">
                                <div className="field">
                                    <label htmlFor="adjustmentType">Type</label>
                                    <select id="adjustmentType" value={adjustmentType} onChange={(e) => setAdjustmentType(e.target.value)}>
                                        <option value="wallet_transaction">Wallet transaction (affects wallet change)</option>
                                        <option value="misc">Misc (affects total ISK only)</option>
                                    </select>
                                </div>
                                <div className="field">
                                    <label htmlFor="adjustmentAmount">Amount (ISK)</label>
                                    <input
                                        id="adjustmentAmount"
                                        type="number"
                                        value={adjustmentAmount}
                                        onChange={(e) => setAdjustmentAmount(e.target.value)}
                                        placeholder="Positive or negative"
                                    />
                                </div>
                                <div className="field">
                                    <label htmlFor="adjustmentReason">Reason</label>
                                    <textarea
                                        id="adjustmentReason"
                                        value={adjustmentReason}
                                        onChange={(e) => setAdjustmentReason(e.target.value)}
                                        placeholder="e.g. late bounty payout, loot sold later"
                                        rows="3"
                                    />
                                </div>
                                <div className="modal-actions">
                                    <button onClick={closeAdjustment} className="btn btn-ghost">Cancel</button>
                                    <button
                                        onClick={addAdjustment}
                                        disabled={!adjustmentAmount || adjustmentAmount === '0'}
                                        className="btn btn-primary"
                                    >
                                        Add adjustment
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default LPRunDetailsModal;
